import React, { useEffect, useState } from 'react';
import { Mail, Linkedin, Calendar, MapPin } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { BrushDivider, EnsoAnimated } from '../components/Motifs';

const Contact = () => {
    const [form, setForm] = useState({ name: '', company: '', email: '', message: '' });

    useEffect(() => {
        document.title = 'Contact — Nakasendo Capital';
    }, []);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = (e) => {
        e.preventDefault();
        const subject = encodeURIComponent(
            `Inquiry from ${form.name || 'Website Visitor'}${form.company ? ` (${form.company})` : ''}`
        );
        const body = encodeURIComponent(
            `Name: ${form.name}\nCompany: ${form.company}\nEmail: ${form.email}\n\nMessage:\n${form.message}`
        );
        window.location.href = `mailto:m@nakasendocapital.com?subject=${subject}&body=${body}`;
    };

    return (
        <div data-testid="contact-page">
            {/* HERO */}
            <section className="relative px-6 md:px-12 lg:px-24 pt-12 md:pt-16 pb-12 overflow-hidden">
                <div
                    aria-hidden="true"
                    style={{ color: 'rgba(48, 58, 82, 0.05)' }}
                    className="absolute -right-6 top-6 font-jp text-[24vw] md:text-[14vw] leading-none pointer-events-none select-none whitespace-nowrap font-light"
                >
                    対話
                </div>
                <div className="relative z-10">
                    <Reveal>
                        <p className="overline">— Let's Talk</p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <h1 className="mt-8 h1-editorial text-[clamp(2.5rem,7vw,7rem)] max-w-[18ch]">
                            Let's shape your <em>equity story.</em>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.35}>
                        <BrushDivider className="w-32 h-3 mt-10" color="#1A1A1A" />
                    </Reveal>
                    <Reveal delay={0.5}>
                        <p className="mt-8 max-w-xl text-ink-soft text-base md:text-lg font-light leading-relaxed">
                            Whether you are preparing for fundraising, exploring a transaction, or
                            seeking the right partner across Asia — we'd be honoured to walk the path with you.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* CONTACT GRID */}
            <section className="relative px-6 md:px-12 lg:px-24 py-16 lg:py-24 border-t border-ink/10 overflow-hidden">
                <EnsoAnimated
                    className="absolute -left-16 -top-10 w-80 h-80 text-aizome opacity-15 hidden lg:block"
                    stroke="#303a52"
                    size={320}
                    delay={0.2}
                />
                <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Direct contact */}
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="overline">— Direct</p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-6 font-serif text-3xl md:text-4xl tracking-tight leading-[1.05]">
                                Reach us<br />
                                <em className="italic font-light">directly.</em>
                            </h2>
                        </Reveal>

                        <div className="mt-12 border-t border-ink/15">
                            <ContactRow
                                icon={<Mail size={18} strokeWidth={1.2} />}
                                label="Email"
                                value="m@nakasendocapital.com"
                                href="mailto:m@nakasendocapital.com"
                                testId="contact-email"
                            />
                            <ContactRow
                                icon={<MapPin size={18} strokeWidth={1.2} />}
                                label="Based in"
                                value="Singapore · Asia"
                                testId="contact-location"
                            />
                        </div>

                        <Reveal delay={0.4}>
                            <div className="mt-12 p-8 border border-ink/15 bg-aizome/[0.02]">
                                <p className="font-jp text-2xl text-aizome">中山道</p>
                                <p className="mt-3 font-serif text-xl italic text-ink leading-snug">
                                    "Growth is a journey best taken with a trusted companion."
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
};

const ContactRow = ({ icon, label, value, href, external, testId }) => {
    const content = (
        <div className="grid grid-cols-12 gap-4 py-6 border-b border-ink/15 group hover:bg-aizome/[0.02] transition-colors duration-300 cursor-pointer">
            <div className="col-span-1 md:col-span-1 text-ink-soft pt-1">{icon}</div>
            <div className="col-span-3 md:col-span-3">
                <p className="overline text-[10px]">{label}</p>
            </div>
            <div className="col-span-8 font-serif text-xl md:text-2xl text-ink group-hover:text-aizome transition-colors duration-300 link-underline inline-block">
                {value}
            </div>
        </div>
    );

    if (!href) {
        return <div data-testid={testId}>{content}</div>;
    }

    return (
        <a
            href={href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            data-testid={testId}
            className="block"
        >
            {content}
        </a>
    );
};

const FieldRow = ({ label, children }) => (
    <div>
        <label className="overline block">{label}</label>
        {children}
    </div>
);

export default Contact;
